import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

// material-ui
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project import
import NavCollapse from './NavCollapse';
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';
import { resolveAssetUrl } from 'utils/assetUrl';

// ==============================|| NAVIGATION - LIST GROUP ||============================== //
// Vzájemně vylučující se rozbalení sousedních "collapse" položek (2026-08-23,
// na přání uživatele - "pokud je otevřen jeden accordion, při požadavku na
// další se původní zavře") - dřív mělo každé NavCollapse VLASTNÍ lokální
// stav, takže "Správce" a "Dokumentace" šly mít otevřené současně. Teď drží
// "který je otevřený" tenhle rodič, stejný princip jako useExclusiveAccordion
// jinde v appce, jen přizpůsobený plain onClick (ne MUI Accordion's
// (event, isExpanded) signature).
//
// Auto-rozbalení podle aktuální URL (2026-08-22, "porad nefunguje nahled
// odkazu v uzkem sidebaru pokud jsem v submenu") PŘESUNUTO sem (2026-08-30,
// real bug fixed - "pokud se otevře jiný accordion, zavřel původní" -
// NavCollapse dřív počítalo `expanded = expandedProp || isActiveRoute`
// samo za sebe, takže manuální kliknutí na JINÝ accordion `expandedCollapseId`
// sice přepnulo, ale ten předchozí zůstal otevřený dál, pokud byl uživatel
// zrovna na jeho aktivní stránce - dvě nezávislé "jsem otevřený" pravidla
// bez vzájemné výhradnosti). Teď je `expandedCollapseId` JEDINÝ zdroj
// pravdy: seedne se/aktualizuje podle aktivní routy PŘI NAVIGACI (efekt
// níže), ale mezi navigacemi ho plně řídí jen ruční klik (handleCollapseToggle)
// - klik na jiný accordion ten předchozí vždy zavře, i když uživatel pořád
// stojí na jeho stránce.
const findActiveCollapseId = (children, pathname) => {
  for (const child of children || []) {
    if (child.type === 'collapse' && containsRoute(child.children, pathname)) {
      return child.id;
    }
  }
  return null;
};

const containsRoute = (children, pathname) =>
  children?.some((child) => child.url === pathname || (child.type === 'collapse' && containsRoute(child.children, pathname))) ?? false;

export default function NavGroup({ item }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const location = useLocation();
  const [expandedCollapseId, setExpandedCollapseId] = useState(() => findActiveCollapseId(item.children, location.pathname));

  useEffect(() => {
    const activeId = findActiveCollapseId(item.children, location.pathname);
    if (activeId) setExpandedCollapseId(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleCollapseToggle = (id) => {
    setExpandedCollapseId((prev) => (prev === id ? null : id));
  };

  const navCollapse = item.children?.map((menuItem) => {
    switch (menuItem.type) {
      case 'collapse':
        return (
          <NavCollapse
            key={menuItem.id}
            item={menuItem}
            level={1}
            expanded={expandedCollapseId === menuItem.id}
            onToggle={() => handleCollapseToggle(menuItem.id)}
          />
        );
      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={1} />;
      default:
        return (
          <Typography key={menuItem.id} variant="h6" sx={{ color: 'error.main', textAlign: 'center' }}>
            Fix - Group Collapse or Items
          </Typography>
        );
    }
  });

  return (
    <List
      subheader={
        item.title &&
        drawerOpen && (
          <Box sx={{ pl: 3, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {item.titleIconUrl && (
              <Box component="img" src={resolveAssetUrl(item.titleIconUrl)} alt="" sx={{ width: 16, height: 16, objectFit: 'contain' }} />
            )}
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {item.title}
            </Typography>
          </Box>
        )
      }
      sx={{ mb: drawerOpen ? 1.5 : 0, py: 0, zIndex: 0 }}
    >
      {navCollapse}
    </List>
  );
}

NavGroup.propTypes = { item: PropTypes.object };
